import React from 'react';
import {
  Box,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { sheetTone } from './sheetStyles';

const RATINGS = [1, 2, 3, 4, 5];

/** A rating cell: the sheet's 1–5 scale, blank until someone picks a value. */
const RatingCell = ({ value, disabled, onCommit, sx }) => (
  <TableCell sx={sx}>
    <TextField
      select
      size="small"
      fullWidth
      disabled={disabled}
      value={value ?? ''}
      onChange={(e) => onCommit(e.target.value === '' ? null : Number(e.target.value))}
      SelectProps={{ displayEmpty: true }}
    >
      <MenuItem value="">
        <em>—</em>
      </MenuItem>
      {RATINGS.map((rating) => (
        <MenuItem key={rating} value={rating}>
          {rating}
        </MenuItem>
      ))}
    </TextField>
  </TableCell>
);

/** A remarks cell that commits on blur so typing doesn't fire a request per key. */
const RemarksCell = ({ value, disabled, onCommit, sx }) => {
  const [draft, setDraft] = React.useState(value || '');
  React.useEffect(() => setDraft(value || ''), [value]);

  return (
    <TableCell sx={sx}>
      <TextField
        size="small"
        fullWidth
        multiline
        disabled={disabled}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          if (draft !== (value || '')) onCommit(draft);
        }}
      />
    </TableCell>
  );
};

/**
 * The manually-filled Work Quality & Quantum table.
 *
 * Dimensions become row groups with a merged first cell, exactly as in the
 * spreadsheet. The sheet's single Remarks/Rating pair is doubled here into an
 * employee self-assessment and a supervisor assessment; which pair is editable
 * comes from the server's `permissions` block.
 *
 * `is_auto` rows (Logged Time, Actual vs Estimated) are computed from tracked
 * time and span the four input columns instead of offering a rating.
 */
const WqqiTable = ({ ratings = [], permissions, onRatingChange }) => {
  const theme = useTheme();
  const colors = sheetTone(theme);
  const supervisorColors = sheetTone(theme, 'supervisor');

  const employeeLocked = !permissions?.can_edit_employee_fields;
  const supervisorLocked = !permissions?.can_edit_supervisor_fields;

  // Group by dimension, preserving the rubric order the API already sorted by.
  const groups = [];
  ratings.forEach((rating) => {
    const last = groups[groups.length - 1];
    if (last && last.dimension === rating.dimension) last.rows.push(rating);
    else groups.push({ dimension: rating.dimension, rows: [rating] });
  });

  const headCell = {
    backgroundColor: colors.header,
    borderColor: colors.border,
    fontWeight: 700,
  };
  const supervisorHeadCell = {
    ...headCell,
    backgroundColor: supervisorColors.header,
  };
  const bodyCell = { borderColor: colors.border, verticalAlign: 'top' };
  const dimensionCell = {
    ...bodyCell,
    fontWeight: 700,
    backgroundColor: theme.palette.action.hover,
    whiteSpace: 'nowrap',
  };

  return (
    <Box sx={{ overflowX: 'auto', mb: 2, border: '1px solid', borderColor: colors.border }}>
      <Table size="small" sx={{ minWidth: 900, '& td, & th': { border: '1px solid' } }}>
        <TableHead>
          <TableRow>
            <TableCell sx={headCell}>Dimension</TableCell>
            <TableCell sx={headCell}>Parameter</TableCell>
            <TableCell sx={headCell}>Employee Remarks</TableCell>
            <TableCell sx={{ ...headCell, width: 90 }}>Rating (5)</TableCell>
            <TableCell sx={supervisorHeadCell}>Supervisor Remarks</TableCell>
            <TableCell sx={{ ...supervisorHeadCell, width: 90 }}>Rating (5)</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.length === 0 && (
            <TableRow>
              <TableCell sx={bodyCell} colSpan={6}>
                <Typography variant="body2" color="text.secondary">
                  This workspace has no WQQI rubric yet.
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {groups.map((group) =>
            group.rows.map((row, index) => (
              <TableRow key={row.id}>
                {index === 0 && (
                  <TableCell sx={dimensionCell} rowSpan={group.rows.length}>
                    {group.dimension}
                  </TableCell>
                )}
                <TableCell sx={bodyCell}>{row.parameter_name}</TableCell>
                {row.is_auto ? (
                  <TableCell sx={{ ...bodyCell, fontVariantNumeric: 'tabular-nums' }} colSpan={4}>
                    <Typography variant="body2">{row.auto_value || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Computed from tracked time
                    </Typography>
                  </TableCell>
                ) : (
                  <>
                    <RemarksCell
                      sx={bodyCell}
                      value={row.employee_remarks}
                      disabled={employeeLocked}
                      onCommit={(value) => onRatingChange(row.id, { employee_remarks: value })}
                    />
                    <RatingCell
                      sx={bodyCell}
                      value={row.employee_rating}
                      disabled={employeeLocked}
                      onCommit={(value) => onRatingChange(row.id, { employee_rating: value })}
                    />
                    <RemarksCell
                      sx={bodyCell}
                      value={row.supervisor_remarks}
                      disabled={supervisorLocked}
                      onCommit={(value) => onRatingChange(row.id, { supervisor_remarks: value })}
                    />
                    <RatingCell
                      sx={bodyCell}
                      value={row.supervisor_rating}
                      disabled={supervisorLocked}
                      onCommit={(value) => onRatingChange(row.id, { supervisor_rating: value })}
                    />
                  </>
                )}
              </TableRow>
            )),
          )}
        </TableBody>
      </Table>
    </Box>
  );
};

export default WqqiTable;
